import { Actions } from '@wahaaabello/redux-async-actions';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, {
  ElementType,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import Swiper from 'react-id-swiper';
import { useDispatch, useSelector } from 'react-redux';
import { getFormValues, SubmissionError } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';
import uuidV4 from 'uuid/v4';
import { requester } from '../../api/config';
import { MODALS } from '../../constants';
import ModalContext from '../../contexts/modal';
import PageBuilderContext from '../../contexts/page-builder';
import PageDataContext from '../../contexts/page-data';
import ProductColorContext from '../../contexts/product-color';
import { useIds, useModalAttrs, useRouteLink } from '../../helpers/hooks';
import pagesActions from '../../store/modules/pages/actions';
import { ApiPageComponent, ApiPageData, RootState } from '../../types';
import ActivityList from '../ActivityList';
import Button from '../Button';
import Copy from '../Copy';
import FlatCard from '../FlatCard';
import { LIGHT_BOX } from '../LightBox';
import MiniCards from '../MiniCards';
import Notice from '../Notice';
import TableRow from '../TableRow';
import Text from '../Text';
import TransactionList from '../TransactionList';
import YouTube from '../YouTube';
import FormBuilder from './FormBuilder';

export type BasePageBuilderProps = {
  initialFormValues?: { [name: string]: any };
  handleClose?: () => void;
};

export type PageBuilderProps = BasePageBuilderProps & {
  pageData: ApiPageData;
};

const PageBuilder = ({
  pageData,
  initialFormValues = {},
  handleClose = () => {}
}: PageBuilderProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const componentIds = useIds(pageData.components.length);
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Actions>>();
  const baseComponentIds = useIds((pageData.baseComponents || []).length);
  const { openModal } = useContext(ModalContext);
  const { extractModalAttrs } = useModalAttrs({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { reload } = useContext(PageDataContext);
  const color = useContext(ProductColorContext);
  const linkTo = useRouteLink();
  const formValuesSelector = useMemo(() => {
    const formComponentIndex = pageData.components.findIndex(
      ({ component }) => component === 'form'
    );
    if (formComponentIndex === undefined) {
      return () => null;
    }
    const formId = componentIds[formComponentIndex];
    return getFormValues(formId);
  }, [componentIds, pageData.components]);
  const formValues = useSelector(formValuesSelector, isEqual);
  const openAfterPage = useCallback(
    state =>
      dispatch(pagesActions.fetchPageRequest(state)).then(pageInfo => {
        const [
          headerTitle,
          headerDescription,
          nextPageData
        ] = extractModalAttrs(pageInfo);
        const closeModal = openModal?.(LIGHT_BOX, {
          headerTitle,
          headerDescription,
          headerColor: color,
          content: (
            <PageBuilder
              pageData={nextPageData}
              handleClose={() => {
                closeModal?.();
                handleClose();
                if (reload) {
                  reload();
                }
              }}
            />
          ),
          useBack: true,
          showOverlayBackgroundColor: false,
          overlayStyle: { zIndex: 300 }
        });
      }),
    [color, dispatch, extractModalAttrs, handleClose, openModal, reload]
  );
  const sendToApi = useCallback(
    data => {
      const { requestInfo, forminfo = {} } = pageData;
      if (!requestInfo) {
        return Promise.resolve();
      }
      const {
        endpoint,
        transformValues = (values: any, restRequestInfo: any) => ({
          forminfo: values,
          ...restRequestInfo
        }),
        after,
        pageTitle,
        previewPageData,
        ...restRequestInfo
      } = requestInfo;
      const dataToPost = transformValues(
        { ...data, ...forminfo },
        restRequestInfo
      );
      return requester
        .post(endpoint, dataToPost, {
          alertMessage: true
        })
        .then(
          (response: any) => {
            setFormSubmitting(false);
            if (response && response.status === 1) {
              let to = linkTo(after);
              if (to && to.state && to.state.pageId) {
                return openAfterPage(to.state);
              }
              if (response.link) {
                to = linkTo(response.link, response.linkdata);
                if (to && to.state && to.state.pageId) {
                  return openAfterPage(to.state);
                }
              }
              if (dataToPost.loadtype && dataToPost.loadtype === 'preview') {
                if (openModal && previewPageData) {
                  const closeModal = openModal(LIGHT_BOX, {
                    headerTitle: pageTitle || '',
                    headerColor: color,
                    content: (
                      <PageBuilder
                        pageData={previewPageData(response)}
                        handleClose={() => {
                          closeModal();
                          handleClose();
                          if (reload) {
                            reload();
                          }
                        }}
                      />
                    ),
                    useBack: true,
                    showOverlayBackgroundColor: false,
                    overlayStyle: { zIndex: 300 }
                  });
                }
                return response;
              }
              handleClose();
              if (reload) {
                reload();
              }
            }
            return response;
          },
          (err: any) => {
            setFormSubmitting(false);
            return Promise.reject(err);
          }
        );
    },
    [handleClose, openAfterPage, pageData, reload, linkTo, openModal, color]
  );

  const handleFormSubmit = useCallback(
    values => {
      const { requestInfo } = pageData;
      if (!requestInfo) {
        return Promise.resolve();
      }
      const {
        endpoint,
        passFormDataTo,
        validate = () => Promise.resolve()
      } = requestInfo;
      if (passFormDataTo) {
        const [pageId, ...restToPass] = passFormDataTo.split('&');
        const options = restToPass.reduce((acc, curr) => {
          const [key, value] = curr.split('=');
          return {
            ...acc,
            [key]: value
          };
        }, {});
        return dispatch(
          pagesActions.fetchPageRequest({
            pageId,
            ...options,
            forminfo: values
          })
        ).then(pageInfo => {
          setFormSubmitting(false);
          const [
            headerTitle,
            headerDescription,
            nextPageData
          ] = extractModalAttrs(pageInfo);
          const formComponentIndex = nextPageData.components.findIndex(
            ({ component }) => component === 'form'
          );
          const formComponent = nextPageData.components[formComponentIndex];
          const closeModal = openModal?.(LIGHT_BOX, {
            headerTitle,
            headerDescription,
            headerColor: color,
            content: (
              <PageBuilder
                pageData={{
                  ...nextPageData,
                  components: [
                    ...nextPageData.components.slice(0, formComponentIndex),
                    {
                      ...formComponent,
                      initialValues: values
                    },
                    ...nextPageData.components.slice(formComponentIndex + 1)
                  ]
                }}
                initialFormValues={values}
                handleClose={() => {
                  closeModal?.();
                  handleClose();
                  if (reload) {
                    reload();
                  }
                }}
              />
            ),
            useBack: true,
            showOverlayBackgroundColor: false,
            overlayStyle: { zIndex: 300 }
          });
        });
      }
      if (endpoint) {
        return validate(values).then(
          () => {
            setFormSubmitting(true);
            return sendToApi(values);
          },
          (errors: any) => new SubmissionError(errors)
        );
      }
      setFormSubmitting(false);
      return undefined;
    },
    [
      reload,
      sendToApi,
      color,
      pageData,
      handleClose,
      dispatch,
      openModal,
      extractModalAttrs
    ]
  );

  const handleBtnClick = useCallback(
    ({
      link,
      onClick = () => {},
      action,
      actionData,
      confirm,
      inlineFormData
    }) => {
      if (link === 'back' || link === 'pop') {
        return handleClose;
      }
      if (action === 'sendActionData') {
        return () => {
          if (confirm) {
            openModal?.(MODALS.CONFIRMATION_MODAL, {
              headerTitle: confirm.title,
              headerDescription: confirm.text,
              action: () => {
                setFormSubmitting(true);
                sendToApi(actionData);
              }
            });
          } else {
            setFormSubmitting(true);
            sendToApi(actionData);
          }
        };
      }
      if (action === 'sendFieldsData' && formValues) {
        return () => {
          setFormSubmitting(true);
          handleFormSubmit({
            ...initialFormValues,
            ...inlineFormData,
            ...formValues
          });
        };
      }
      return onClick;
    },
    [
      handleClose,
      openModal,
      sendToApi,
      handleFormSubmit,
      formValues,
      initialFormValues
    ]
  );

  const renderFlatCard = useCallback(
    (componentProps, { key = uuidV4(), padded = true } = {}) => (
      <FlatCard
        key={key}
        {...{
          ...componentProps,
          containerClassName: classNames(
            { 'py-6 px-4': padded },
            componentProps.containerClassName,
            'flex-shrink-0'
          ),
          iconLeft: {
            ...componentProps.iconLeft,
            marginRight: '0.5rem'
          }
        }}
      />
    ),
    []
  );

  const renderFlatList = useCallback(
    (componentProps, { key = uuidV4() } = {}) => {
      const { listData, ...flatCardProps } = componentProps;
      return (
        <div key={key}>
          {listData.map((otherFlatCardProps: any) =>
            renderFlatCard({
              ...flatCardProps,
              ...otherFlatCardProps,
              marginBottom: '1.5rem'
            })
          )}
        </div>
      );
    },
    [renderFlatCard]
  );

  const renderButton = useCallback(
    (componentProps, { key = uuidV4() } = {}) => {
      const {
        containerClassName = '',
        title,
        container = {},
        fill = false,
        action,
        as = 'button',
        color: incomingColor,
        bordered = true,
        ...rest
      } = componentProps;
      const btnProps: {
        as?: ElementType;
        htmlFor?: string;
        loading?: boolean;
      } = {};
      if (action === 'sendActionData' || action === 'sendFieldsData') {
        btnProps.loading = formSubmitting;
      } else {
        btnProps.as = as;
      }
      return (
        <div
          key={key}
          style={container}
          className={classNames(containerClassName)}
        >
          <Button
            {...rest}
            {...btnProps}
            variant={fill ? 'primary' : 'secondary'}
            bordered={bordered && !fill}
            block
            onClick={handleBtnClick(componentProps)}
            color={incomingColor || color}
          >
            {title}
          </Button>
        </div>
      );
    },
    [formSubmitting, color, handleBtnClick]
  );

  const renderForm = useCallback(
    ({ formFields, formId, ...rest }, { key }) => {
      return (
        <FormBuilder
          {...rest}
          key={key}
          ref={formRef}
          initialValues={{ ...initialFormValues, ...formValues }}
          formId={formId || key}
          formFields={formFields}
          submit={handleFormSubmit}
          showSubmitBtn={false}
          scroll={false}
        />
      );
    },
    [formValues, handleFormSubmit, initialFormValues]
  );

  const renderComponents = useCallback(
    (componentsData: ApiPageComponent[], ids) =>
      componentsData.map(({ component, ...componentProps }, index) => {
        switch (component) {
          case 'youtube': {
            const { videoId, height } = componentProps;
            return (
              <YouTube
                videoId={videoId}
                opts={{
                  height
                }}
              />
            );
          }
          case 'table_row': {
            const { tableData, ...style } = componentProps;
            return <TableRow tableData={tableData} containerStyle={style} />;
          }
          case 'full_image': {
            let { url, link, width, marginLeft, ...style } = componentProps;
            if (link) {
              const backgroundImage = url;
              const height = url.includes('withdraw-to-bank')
                ? '149px'
                : '350px';
              marginLeft = 0;
              width = '100%';
              componentProps = {
                ...componentProps,
                backgroundImage,
                height,
                marginLeft,
                width
              };
              return renderFlatCard(componentProps, { key: ids[index] });
            } else {
              return <img src={url} alt="Depiction" style={style} />;
            }
          }
          case 'element': {
            const { element } = componentProps;
            return element;
          }
          case 'notice': {
            const { notice } = componentProps;
            return <Notice {...notice} />;
          }
          case 'mini_cards': {
            const { data, ...rest } = componentProps;
            return <MiniCards key={ids[index]} minicards={data} {...rest} />;
          }
          case 'copy': {
            const { info } = componentProps;
            return <Copy key={ids[index]} info={info} />;
          }
          case 'swipe_list': {
            const { listData, backgroundImage, feel } = componentProps;
            return (
              <Swiper
                key={ids[index]}
                containerClass="w-full h-full overflow-x-hidden relative"
                // @ts-ignore
                navigation={{
                  nextEl: '.swiper-button-next.scale-60.right-0',
                  prevEl: '.swiper-button-prev.scale-60.left-0'
                }}
                pagination={{
                  el: '.swiper-pagination',
                  clickable: true,
                  dynamicBullets: true
                }}
              >
                {(listData as {
                  image: { container: any; uri: string; styled: any };
                  [key: string]: any;
                }[]).map(({ image, ...rest }) => (
                  <div
                    className="flex flex-col justify-center"
                    key={rest.title.text + rest.subTitle.text}
                  >
                    {image && (
                      <div
                        className="flex-shrink-0 flex flex-col"
                        style={image.container}
                      >
                        <img
                          src={image.uri}
                          alt=""
                          style={{
                            ...image.styled,
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    )}
                    {renderFlatCard(
                      {
                        ...rest,
                        feel: {
                          ...feel,
                          ...rest.feel,
                          marginBottom: '1.5rem'
                        },
                        bordered: !image,
                        backgroundImage
                      },
                      {
                        padded: !image
                      }
                    )}
                  </div>
                ))}
              </Swiper>
            );
          }
          case 'text': {
            const { text, link, ...restProps } = componentProps;
            if (link) {
              return renderButton(
                {
                  title: text,
                  link,
                  bordered: false,
                  ...restProps
                },
                { key: ids[index] }
              );
            }
            return (
              <Text key={ids[index]} {...restProps}>
                {text}
              </Text>
            );
          }
          case 'flat_card': {
            return renderFlatCard(componentProps, { key: ids[index] });
          }
          case 'flat_list': {
            return renderFlatList(componentProps, { key: ids[index] });
          }
          case 'transaction_list': {
            const { listData, ...containerStyle } = componentProps;
            return (
              <TransactionList
                listData={listData}
                containerStyle={containerStyle}
              />
            );
          }
          case 'activity_list': {
            const { listData, ...containerStyle } = componentProps;
            return (
              <ActivityList
                listData={listData}
                containerStyle={containerStyle}
              />
            );
          }
          case 'button': {
            return renderButton(componentProps, { key: ids[index] });
          }
          case 'form': {
            return renderForm(componentProps, { key: ids[index] });
          }
          default:
            return null;
        }
      }),
    [renderButton, renderFlatCard, renderFlatList, renderForm]
  );

  const pageRef = useRef<HTMLDivElement | null>(null);

  return (
    <PageBuilderContext.Provider value={{ pageRef }}>
      {!!(pageData.components || []).length && (
        <div
          className="flex flex-col flex-1 px-6 md:px-8 overflow-y-scroll relative pb-10"
          ref={pageRef}
        >
          {renderComponents(pageData.components || [], componentIds)}
        </div>
      )}
      {!!(pageData.baseComponents || []).length && (
        <div className="px-6 md:px-8 py-2 md:pb-4 shadow-dark z-10">
          {renderComponents(pageData.baseComponents || [], baseComponentIds)}
        </div>
      )}
    </PageBuilderContext.Provider>
  );
};

export default PageBuilder;
